import { useEffect } from 'react'

type SeoInput = {
  title: string
  description: string
  path: string
}

const SITE_ORIGIN = 'https://goelst-international.com'

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Sets an indexable, descriptive document head for each page:
 * unique title, unique meta description, canonical, robots index,follow.
 */
export function useSeo({ title, description, path }: SeoInput) {
  useEffect(() => {
    document.title = title
    document.documentElement.lang = 'nl'

    upsertMeta('meta[name="description"]', 'name', 'description', description)
    upsertMeta('meta[name="robots"]', 'name', 'robots', 'index, follow, max-image-preview:large')
    upsertMeta('meta[name="viewport"]', 'name', 'viewport', 'width=device-width, initial-scale=1')
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title)
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description)
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'nl_NL')
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', `${SITE_ORIGIN}${path}`)
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')

    upsertLink('canonical', `${window.location.origin}${path}`)
  }, [title, description, path])
}

export function useLocalBusinessSchema() {
  useEffect(() => {
    const id = 'goelst-localbusiness-schema'
    if (document.getElementById(id)) return

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Goelst International B.V.',
      description:
        'Goelst International ontwikkelt en produceert hoogwaardige gordijnrailsystemen voor architecten, interieurontwerpers en professionele projecten.',
      url: 'https://goelst-international.com/',
      telephone: '+31333035964',
      email: 'info@goelst-international.com',
      image: 'https://cdn.magicpatterns.com/uploads/x8vLnVD3YoYc8XE48QnjiF/aaLogo.jpg',
      logo: 'https://cdn.magicpatterns.com/uploads/x8vLnVD3YoYc8XE48QnjiF/aaLogo.jpg',
      foundingDate: '1978',
      vatID: undefined,
      identifier: { '@type': 'PropertyValue', name: 'KVK', value: '62012592' },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Modemweg 19',
        postalCode: '3821 BS',
        addressLocality: 'Amersfoort',
        addressRegion: 'Utrecht',
        addressCountry: 'NL',
      },
      sameAs: ['https://www.instagram.com/goelst_international/'],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
    }

    const script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    script.text = JSON.stringify(schema)
    document.head.appendChild(script)
  }, [])
}
